import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Slide,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { Pagination } from '@material-ui/lab'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { ChangeModal } from '../../../../components/ChangeModal'
import {
  CREATE_IP_ACCESS_RULE_MUTATION,
  DELETE_IP_ACCESS_RULE_MUTATION,
  GET_IP_ACCESS_RULES_QUERY,
  UPDATE_IP_ACCESS_RULE_MUTATION
} from '../../../../graphql/ipAccessRules'
import { getDirection, getTranslate } from '../../../../localization'
import { IPAccessRule, IPAccessRuleForm } from '../../../../types/ipAccessRules'
import useStyles from './index.styles'

const emptyForm: IPAccessRuleForm = {
  scope: 'Global',
  effect: 'Deny',
  network: '',
  enabled: true,
  reason: '',
  entityType: 'Tenant',
  entityId: '',
  entityCode: '',
  userId: ''
}

type RulesData = {
  ipAccessRules: {
    rules: IPAccessRule[]
    pagination: { page: number; perPage: number; total: number }
  }
}

export default function IPAccessRules() {
  const classes = useStyles()
  const [form, setForm] = useState<IPAccessRuleForm>(emptyForm)
  const [editing, setEditing] = useState<IPAccessRule>()
  const [deleting, setDeleting] = useState<IPAccessRule>()
  const [modalOpen, setModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const variables = useMemo(
    () => ({ input: { pagination: { page, perPage: pageSize } } }),
    [page]
  )
  const { data, loading, error, refetch } = useQuery<RulesData>(
    GET_IP_ACCESS_RULES_QUERY,
    {
      variables,
      fetchPolicy: 'network-only',
      onError: () => toast.error(getTranslate('Unable to load IP access rules'))
    }
  )

  const finishMutation = () => {
    setModalOpen(false)
    setEditing(undefined)
    setForm(emptyForm)
    refetch()
  }
  const [createRule, { loading: creating }] = useMutation(
    CREATE_IP_ACCESS_RULE_MUTATION,
    {
      onCompleted: () => {
        toast.success(getTranslate('IP access rule created'))
        finishMutation()
      },
      onError: () =>
        toast.error(getTranslate('Unable to create IP access rule'))
    }
  )
  const [updateRule, { loading: updating }] = useMutation(
    UPDATE_IP_ACCESS_RULE_MUTATION,
    {
      onCompleted: () => {
        toast.success(getTranslate('IP access rule updated'))
        finishMutation()
      },
      onError: () =>
        toast.error(getTranslate('Unable to update IP access rule'))
    }
  )
  const [deleteRule, { loading: deletingRule }] = useMutation(
    DELETE_IP_ACCESS_RULE_MUTATION,
    {
      onCompleted: () => {
        toast.success(getTranslate('IP access rule deleted'))
        setDeleting(undefined)
        refetch()
      },
      onError: () =>
        toast.error(getTranslate('Unable to delete IP access rule'))
    }
  )

  const valid =
    form.network.trim() !== '' &&
    (form.scope !== 'Entity' ||
      (form.entityId.trim() !== '' && form.entityCode.trim() !== '')) &&
    (form.scope !== 'User' || form.userId.trim() !== '')

  const openCreate = () => {
    setEditing(undefined)
    setForm(emptyForm)
    setModalOpen(true)
  }
  const openEdit = (rule: IPAccessRule) => {
    setEditing(rule)
    setForm({
      scope: rule.scope,
      effect: rule.effect,
      network: rule.canonicalCidr,
      enabled: rule.enabled,
      reason: rule.reason,
      entityType: rule.entityType || 'Tenant',
      entityId: rule.entityId || '',
      entityCode: rule.entityCode || '',
      userId: rule.userId || ''
    })
    setModalOpen(true)
  }
  const submit = () => {
    if (editing) {
      updateRule({
        variables: {
          input: {
            ruleId: editing.id,
            effect: form.effect,
            network: form.network.trim(),
            enabled: form.enabled,
            reason: form.reason.trim() || null,
            expectedRevision: editing.revision
          }
        }
      })
      return
    }
    createRule({
      variables: {
        input: {
          scope: form.scope,
          effect: form.effect,
          network: form.network.trim(),
          enabled: form.enabled,
          reason: form.reason.trim() || null,
          entityType: form.scope === 'Entity' ? form.entityType : null,
          entityId: form.scope === 'Entity' ? form.entityId.trim() : null,
          entityCode: form.scope === 'Entity' ? form.entityCode.trim() : null,
          userId: form.scope === 'User' ? form.userId.trim() : null
        }
      }
    })
  }

  return (
    <section className={classes.root}>
      <div className={classes.heading}>
        <div>
          <Typography variant="h4" className={classes.title}>
            {getTranslate('IP Access Rules')}
          </Typography>
          <Typography variant="h5" className={classes.subtitle}>
            {getTranslate(
              'Deny rules take precedence. Allow lists are cumulative across global, entity and user scopes.'
            )}
          </Typography>
        </div>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}>
          {getTranslate('Add Rule')}
        </Button>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={32} />
        </div>
      ) : error ? (
        <div className={classes.error}>
          <Typography color="error">
            {getTranslate('Unable to load IP access rules')}
          </Typography>
          <Button color="primary" onClick={() => refetch()}>
            {getTranslate('Retry')}
          </Button>
        </div>
      ) : data?.ipAccessRules.rules.length ? (
        <div className={classes.tableWrapper}>
          <Table size="small" aria-label={getTranslate('IP Access Rules')}>
            <TableHead>
              <TableRow>
                <TableCell>{getTranslate('Scope')}</TableCell>
                <TableCell>{getTranslate('Effect')}</TableCell>
                <TableCell>{getTranslate('Network')}</TableCell>
                <TableCell>{getTranslate('Target')}</TableCell>
                <TableCell>{getTranslate('Status')}</TableCell>
                <TableCell>{getTranslate('Reason')}</TableCell>
                <TableCell align="right">{getTranslate('Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.ipAccessRules.rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{getTranslate(rule.scope)}</TableCell>
                  <TableCell>
                    <Typography
                      className={
                        rule.effect === 'Allow'
                          ? classes.effectAllow
                          : classes.effectDeny
                      }>
                      {getTranslate(rule.effect)}
                    </Typography>
                  </TableCell>
                  <TableCell>{rule.canonicalCidr}</TableCell>
                  <TableCell>
                    {rule.scope === 'Entity'
                      ? `${rule.entityType}: ${rule.entityCode}`
                      : rule.scope === 'User'
                      ? rule.userId
                      : getTranslate('All users')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={rule.enabled ? 'primary' : 'default'}
                      label={getTranslate(
                        rule.enabled ? 'Enabled' : 'Disabled'
                      )}
                    />
                  </TableCell>
                  <TableCell>{rule.reason || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={getTranslate('Edit')}
                      onClick={() => openEdit(rule)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={getTranslate('Delete')}
                      onClick={() => setDeleting(rule)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.ipAccessRules.pagination.total > pageSize && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(data.ipAccessRules.pagination.total / pageSize)}
              page={page}
              onChange={(_, nextPage) => setPage(nextPage)}
              color="primary"
            />
          )}
        </div>
      ) : (
        <Typography className={classes.empty}>
          {getTranslate(
            'No IP access rules configured. All IPv4 addresses are allowed.'
          )}
        </Typography>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        className={classes.modal}
        dir={getDirection()}>
        <Slide direction="down" in={modalOpen}>
          <Paper className={classes.paper}>
            <Typography variant="h3">
              {getTranslate(
                editing ? 'Edit IP Access Rule' : 'Add IP Access Rule'
              )}
            </Typography>
            <div className={classes.form}>
              <SelectWithLabel
                label={getTranslate('Scope')}
                value={form.scope}
                disabled={!!editing}
                onChange={(event: { target: { value: unknown } }) =>
                  setForm({
                    ...form,
                    scope: event.target.value as IPAccessRuleForm['scope']
                  })
                }>
                {['Global', 'Entity', 'User'].map((scope) => (
                  <MenuItem key={scope} value={scope}>
                    {getTranslate(scope)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <SelectWithLabel
                label={getTranslate('Effect')}
                value={form.effect}
                onChange={(event: { target: { value: unknown } }) =>
                  setForm({
                    ...form,
                    effect: event.target.value as IPAccessRuleForm['effect']
                  })
                }>
                {['Allow', 'Deny'].map((effect) => (
                  <MenuItem key={effect} value={effect}>
                    {getTranslate(effect)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <InputWithLabel
                label={getTranslate('IPv4 address or CIDR')}
                helpertext={getTranslate(
                  'Examples: 203.0.113.10 or 203.0.113.0/24'
                )}
                value={form.network}
                onChange={(event) =>
                  setForm({ ...form, network: event.currentTarget.value })
                }
              />
              {form.scope === 'Entity' && (
                <>
                  <SelectWithLabel
                    label={getTranslate('Entity Type')}
                    value={form.entityType}
                    disabled={!!editing}
                    onChange={(event: { target: { value: unknown } }) =>
                      setForm({
                        ...form,
                        entityType: event.target
                          .value as IPAccessRuleForm['entityType']
                      })
                    }>
                    <MenuItem value="Tenant">{getTranslate('Tenant')}</MenuItem>
                    <MenuItem value="Provider">
                      {getTranslate('Provider')}
                    </MenuItem>
                  </SelectWithLabel>
                  <InputWithLabel
                    label={getTranslate('Entity ID')}
                    value={form.entityId}
                    disabled={!!editing}
                    onChange={(event) =>
                      setForm({ ...form, entityId: event.currentTarget.value })
                    }
                  />
                  <InputWithLabel
                    label={getTranslate('Entity Code')}
                    value={form.entityCode}
                    disabled={!!editing}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        entityCode: event.currentTarget.value
                      })
                    }
                  />
                </>
              )}
              {form.scope === 'User' && (
                <InputWithLabel
                  label={getTranslate('User ID')}
                  value={form.userId}
                  disabled={!!editing}
                  onChange={(event) =>
                    setForm({ ...form, userId: event.currentTarget.value })
                  }
                />
              )}
              <InputWithLabel
                className={classes.fullWidth}
                label={getTranslate('Reason')}
                value={form.reason}
                onChange={(event) =>
                  setForm({ ...form, reason: event.currentTarget.value })
                }
              />
              <FormControlLabel
                className={classes.switchRow}
                label={getTranslate('Enabled')}
                control={
                  <Switch
                    inputProps={{
                      'aria-label': getTranslate('Enabled')
                    }}
                    color="primary"
                    checked={form.enabled}
                    onChange={(event) =>
                      setForm({ ...form, enabled: event.target.checked })
                    }
                  />
                }
              />
            </div>
            <div className={classes.actions}>
              <Button onClick={() => setModalOpen(false)}>
                {getTranslate('Cancel')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={!valid || creating || updating}
                onClick={submit}>
                {getTranslate(editing ? 'Save' : 'Add Rule')}
                {(creating || updating) && <CircularProgress size={16} />}
              </Button>
            </div>
          </Paper>
        </Slide>
      </Modal>

      <ChangeModal
        title="Delete this IP access rule?"
        subtitle="This rule will stop affecting future logins."
        confirmLabel="Delete"
        loading={deletingRule}
        disabled={deletingRule}
        isOpen={!!deleting}
        onClose={() => setDeleting(undefined)}
        onClick={() => {
          if (!deleting) return
          deleteRule({
            variables: {
              input: {
                ruleId: deleting.id,
                expectedRevision: deleting.revision
              }
            }
          })
        }}
      />
    </section>
  )
}
